/**
 * LISA Online-Anamnese
 * Externe Anamnese-App fuer Patienten (per Email-Link)
 *
 * Backend: Hetzner FastAPI (lisa-api.dr-hezel.de)
 * Verschluesselung: AES-256-GCM + RSA-OAEP im Browser
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import { validateToken, TokenError } from './services/api';

// Pages
import AnamnesePage from './pages/AnamnesePage';
import TokenInvalid from './pages/TokenInvalid';
import SubmitSuccess from './pages/SubmitSuccess';

// Theme (dr-hezel.de Farbschema - Blau, Mobile-optimiert)
const theme = createTheme({
  palette: {
    primary: {
      main: '#1a6ead',
      light: '#5292c8',
      dark: '#004c80',
      contrastText: '#fff'
    },
    secondary: {
      main: '#80deea',
      light: '#b4ffff',
      dark: '#4bacb8'
    },
    success: {
      main: '#66bb6a',
      light: '#98ee99',
      dark: '#338a3e'
    },
    warning: {
      main: '#ffa726',
      light: '#ffd95b',
      dark: '#c77800'
    },
    error: {
      main: '#ef5350',
      light: '#ff867c',
      dark: '#b61827'
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff'
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b'
    }
  },
  typography: {
    fontFamily: '"Inter", "Montserrat", "Raleway", "Helvetica", sans-serif',
    h1: { fontWeight: 600, color: '#1e293b' },
    h2: { fontWeight: 600, color: '#1e293b' },
    h3: { fontWeight: 600, color: '#1e293b' },
    h4: { fontWeight: 600, color: '#1e293b' },
    h5: { fontWeight: 600, color: '#1e293b' },
    h6: { fontWeight: 600, color: '#1e293b' },
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.5 }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
          padding: '10px 24px',
          minHeight: 48
        },
        contained: {
          boxShadow: '0 2px 4px rgba(26, 110, 173, 0.2)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(26, 110, 173, 0.3)'
          }
        }
      }
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8 }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8 }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 12
        }
      }
    }
  },
  shape: { borderRadius: 8 }
});

/**
 * Token-Validierung Komponente
 * Prueft Token via Hetzner API und laedt Patienten-Daten + Public Key
 */
const TokenValidator = () => {
  const [status, setStatus] = useState('loading');
  const [submissionData, setSubmissionData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    doValidateToken();
  }, []);

  const doValidateToken = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      if (!token) {
        setStatus('invalid');
        setError('Kein Token angegeben');
        return;
      }

      // Token via Hetzner API validieren
      const data = await validateToken(token);

      // Token ist gueltig — Daten aufbereiten
      setSubmissionData({
        token,
        publicKey: data.public_key,
        keyVersion: data.key_version,
        patientId: data.patient_id,
        praxisName: 'Zahnarztpraxis Dr. Hezel',
        terminDatum: data.termin_datum || null,
        isNewPatient: data.is_new_patient || !data.patient_id,
        patientName: data.patient_name || null,
        patientGeburtsdatum: data.patient_geburtsdatum || null,
        patientEmail: data.patient_email || null,
        patientGeschlecht: data.patient_geschlecht || null,
        lokalanaesthesie: data.lokalanaesthesie || false,
        ausfallhonorar: data.ausfallhonorar || false
      });
      setStatus('valid');

    } catch (err) {
      if (err instanceof TokenError) {
        // Strukturierter Fehler vom Backend
        if (err.reason === 'expired') {
          setStatus('expired');
          setError('Dieser Link ist abgelaufen. Bitte fordern Sie einen neuen Link an.');
        } else if (err.reason === 'submitted') {
          setStatus('submitted');
          setError('Die Anamnese wurde bereits ausgefuellt.');
        } else {
          setStatus('invalid');
          setError('Ungueltiger oder abgelaufener Link');
        }
      } else {
        console.error('Fehler bei Token-Validierung:', err);
        setStatus('invalid');
        setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es spaeter erneut.');
      }
    }
  };

  if (status === 'loading') {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress color="primary" />
        <Typography>Anamnese wird geladen...</Typography>
      </Box>
    );
  }

  if (status === 'invalid' || status === 'expired' || status === 'submitted') {
    return <TokenInvalid reason={status} message={error} />;
  }

  return <AnamnesePage submissionData={submissionData} />;
};

/**
 * Success Route Handler
 */
const SuccessRoute = () => {
  const submitted = sessionStorage.getItem('anamnese_submitted');
  if (!submitted) {
    return <Navigate to="/" replace />;
  }
  return <SubmitSuccess />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<TokenValidator />} />
          <Route path="/success" element={<SuccessRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
